import { UpsellFlow, UpsellStepsFlow } from '@models';
import {
  getAllUpsellFlow,
  getOffersByUpsellFlow,
} from '@service/api/upsellFlow';
import { updateUpsellStepFlow } from '@service/api/upsellStepFlow/updateUpsellStepFlow';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { XYPosition } from 'react-flow-renderer';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useBreadcrumbs } from 'src/features/layout/hooks/useBreadcrumbs';
import { ConfirmModalProps } from 'src/features/upsellFunnel/components/ConfirmModal';
import { ManageEdgeModalProps } from 'src/features/upsellFunnel/components/ManageEdgeModal';
import { ManageStepModalProps } from 'src/features/upsellFunnel/components/ManageStepModal';
import {
  getAllUpsellStepFlow,
  removeUpsellStepFlow,
} from 'src/service/api/upsellStepFlow';

export type TUpsellStepsFlowManage = UpsellStepsFlow.IUpsellStepsFlow & {
  changed: boolean;
  position?: XYPosition;
};

export function useUpsellStepFlow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flowSteps, setFlowSteps] = useState<
    UpsellStepsFlow.IUpsellStepsFlow[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [changesSteps, setChangesSteps] = useState<TUpsellStepsFlowManage[]>(
    []
  );
  const [deletedSteps, setDeletedSteps] = useState<TUpsellStepsFlowManage[]>(
    []
  );
  const [manageStepModal, setManageStepModal] =
    useState<ManageEdgeModalProps | null>(null);

  const [deleteStepModal, setDeleteStepModal] =
    useState<ConfirmModalProps | null>(null);
  const [createStepModal, setCreateStepModal] =
    useState<ManageStepModalProps | null>(null);
  const [isOpenManageFlowDrawer, setIsOpenManageFlowDrawer] = useState(false);

  const flowIdFromQuery = Number(searchParams.get('flowId'));

  const [upsellFlow, setUpsellFlow] = useState<UpsellFlow.IUpsellFlow | null>(
    null
  );

  useEffect(() => {
    const loadUpsellFlow = async () => {
      if (!flowIdFromQuery) return;

      try {
        setIsLoading(true);

        const { rows } = await getAllUpsellFlow({
          page: 1,
          limit: 1,
          filter: { filter: flowIdFromQuery },
        });

        setUpsellFlow(rows[0] ?? null);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadUpsellFlow();
  }, [flowIdFromQuery]);

  const updateFlowSteps = useCallback(async () => {
    try {
      if (!flowIdFromQuery) return;
      setIsLoading(true);
      const steps = await getAllUpsellStepFlow(flowIdFromQuery);
      setFlowSteps(steps);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [flowIdFromQuery]);

  useEffect(() => {
    updateFlowSteps();
  }, [updateFlowSteps]);

  const { data: flowOffers, isLoading: isLoadingManageFlowDrawerTable } =
    useQuery({
      queryKey: ['getOffersByUpsellFlow', flowIdFromQuery],
      queryFn: async () => await getOffersByUpsellFlow(flowIdFromQuery),
      enabled: !!flowIdFromQuery && isOpenManageFlowDrawer,
      refetchOnWindowFocus: false,
    });

  const breadcrumbs = useMemo(() => {
    return [
      {
        label: 'Upsell',
      },
      {
        label: 'Produtos',
        route: '/upsell/funnel?tab=flows',
      },
      {
        label: `Fluxo de ${upsellFlow?.name || ''}`,
      },
    ];
  }, [upsellFlow?.name]);

  const updateBreadcrumbs = useBreadcrumbs(breadcrumbs);

  useEffect(() => {
    updateBreadcrumbs(breadcrumbs);
  }, [breadcrumbs, updateBreadcrumbs]);

  const addChangeStep = useCallback(
    (changedStep: TUpsellStepsFlowManage) => {
      const findChangeSteps = changesSteps.find(
        oldStep => changedStep.id === oldStep.id
      );

      setChangesSteps(
        !findChangeSteps
          ? [...changesSteps, changedStep]
          : changesSteps.map(oldStep =>
              changedStep.id === oldStep.id ? changedStep : oldStep
            )
      );
    },
    [changesSteps]
  );

  const onOpenManageStepModal = useCallback(
    (stepSource: Pick<ManageEdgeModalProps, 'stepFlow' | 'toStepFlow'>) => {
      setManageStepModal({
        ...stepSource,
        onCloseModal: () => setManageStepModal(null),
        onSelectFinish: addChangeStep,
      });
    },
    [addChangeStep]
  );

  const saveUpdatesPage = useCallback(async () => {
    try {
      setIsLoading(true);

      await updateUpsellStepFlow(changesSteps);
      await removeUpsellStepFlow(deletedSteps.map(({ id }) => id));

      navigate('/upsell/funnel');
    } catch (err) {
      console.error('Erro ao salvar', err);
    } finally {
      setIsLoading(false);
    }
  }, [changesSteps, navigate, deletedSteps]);

  const onRemoveStep = useCallback(
    (step: TUpsellStepsFlowManage) => {
      setDeletedSteps(oldState => [...oldState, step]);
    },
    [changesSteps, navigate]
  );

  const onOpenModalDeleteStep = useCallback(
    (step: TUpsellStepsFlowManage) => {
      setDeleteStepModal({
        onCloseModal: () => setDeleteStepModal(null),
        onConfirmDelete: () => onRemoveStep(step),
        titleConfirm: 'Deseja realmente excluir?',
        textConfirm:
          'Você ainda poderá recarregar a tela para desfazer essa ação, apenas antes de APLICAR',
      });
    },
    [onRemoveStep]
  );

  const onOpenStepCreateModal = useCallback(() => {
    if (!flowIdFromQuery) return;

    setCreateStepModal({
      flowId: flowIdFromQuery,
      onCloseModal: () => setCreateStepModal(null),
      onCreateFinish: updateFlowSteps,
    });
  }, [flowIdFromQuery, updateFlowSteps]);

  const onOpenModalEditStep = useCallback(
    (editStep: TUpsellStepsFlowManage) => {
      if (!flowIdFromQuery) return;

      setCreateStepModal({
        flowId: flowIdFromQuery,
        onCloseModal: () => setCreateStepModal(null),
        onCreateFinish: updateFlowSteps,
        editMode: {
          onEditFinish: addChangeStep,
          stepEdit: editStep,
        },
      });
    },
    [flowIdFromQuery, updateFlowSteps, addChangeStep]
  );

  const onOpenManageFlowDrawer = useCallback(() => {
    setIsOpenManageFlowDrawer(true);
  }, []);

  const onCloseManageFlowDrawer = useCallback(() => {
    setIsOpenManageFlowDrawer(false);
  }, []);

  const allFlowSteps = useMemo<TUpsellStepsFlowManage[]>(() => {
    return flowSteps
      .map(step => {
        const changeStep = changesSteps.find(({ id }) => step.id === id);
        if (changeStep) {
          return {
            ...changeStep,
            changed: true,
          };
        }

        return {
          ...step,
          changed: false,
        };
      })
      .filter(
        step => !deletedSteps.find(deleteStep => deleteStep.id === step.id)
      )
      .map(step => ({
        ...step,
        up: !deletedSteps.find(deleteStep => deleteStep.id === step.up)
          ? step.up
          : null,
        down: !deletedSteps.find(deleteStep => deleteStep.id === step.down)
          ? step.down
          : null,
      }));
  }, [flowSteps, changesSteps, deletedSteps]);

  return {
    upsellFlow,
    allFlowSteps,
    isLoading,
    onOpenStepCreateModal,
    onOpenManageStepModal,
    manageStepModal,
    addChangeStep,
    saveUpdatesPage,
    onOpenModalDeleteStep,
    deleteStepModal,
    createStepModal,
    onOpenModalEditStep,
    isOpenManageFlowDrawer,
    onOpenManageFlowDrawer,
    onCloseManageFlowDrawer,
    flowOffers,
    isLoadingManageFlowDrawerTable,
    navigate,
  };
}
